import dynamic from 'next/dynamic';
import get from 'lodash/get';
import ContentfulParser from 'components/BlogCommon/Article/ContentfulParser';
import Illustration from 'UI/components/Illustration';
import Typography from 'UI/components/Typography';
import { TYPOGRAPHY_SIZE, TYPOGRAPHY_TAGS } from 'UI/components/Typography/utils/useTypography';
import CardContainer from 'UI/containers/CardContainer';
import { getDocumentFields, getImage } from 'utils/helper';
import OverlapCard from 'UI/components/Cards/OverlapCard';
import useCard from './utils/useCard';
import styles from './Card.module.scss';

const Svg = dynamic(() => import('UI/components/Svg'), { ssr: false });

const getNestedCardProps = (module) => {
  if (module?.fields) {
    const {
      title,
      text,
      description,
      images,
      contentList,
      contentModules,
      subtitle,
    } = getDocumentFields(module, [
      'title',
      'text',
      'description',
      'images',
      'contentList',
      'contentModules',
      'subtitle',
    ]);

    return {
      title,
      text,
      description,
      image: getImage(get(images, '[0]')),
      icon: get(contentList, '[0]'),
      contentModules,
      subtitle,
    };
  }

  return {
    ...module,
  };
};

const Card = (props) => {
  const {
    Wrapper,
    icon,
    title,
    text,
    children,
    className,
    image,
    withoutBackground,
    items,
    svgWithWrapper,
    contentModules,
    specificCardOverlay,
    ...rest
  } = useCard(props);

  return (
    <Wrapper {...rest}>
      <CardContainer
        className={className}
        noBackground={withoutBackground}
      >
        {!!image?.url && (
          <Illustration
            className={styles.image}
            src={image.url}
            alt={image.alt}
            transparent
          />
        )}

        {icon && (svgWithWrapper
          ? (
            <div className={styles.iconWrapper}>
              <Svg
                type={icon}
                className={styles.icon}
              />
            </div>
          ) : (
            <Svg
              type={icon}
              className={styles.icon}
            />
          ))}

        <div className={styles.content}>
          {title && (
            <Typography
              variant={TYPOGRAPHY_TAGS.h3}
              size={TYPOGRAPHY_SIZE.headline20}
              isBold
              className={styles.title}
            >
              {title}
            </Typography>
          )}

          <div className={styles.cardContent}>
            <ContentfulParser document={text} />

            {(!!items?.length) && (
              <div className={styles.itemList}>
                {items.map((item) => (
                  <div
                    key={item.title}
                    className={styles.item}
                  >
                    <Svg
                      type={item.contentList?.[0]}
                      className={styles.itemIcon}
                    />

                    <span>
                      {item.title}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <span
            data-read-more
            className={styles.readMore}
          >
            Read more
          </span>
        </div>

        {!!contentModules?.length && (
          <div className={styles.contentModules}>
            {contentModules.map((module, index) => {
              const {
                ...cardProps
              } = getNestedCardProps(module);
              const key = cardProps.title || index;

              if (specificCardOverlay) {
                return (
                  <OverlapCard
                    key={key}
                    className={styles.overlapCard}
                    image={cardProps.image}
                    title={cardProps.title}
                    text={cardProps.text}
                    description={cardProps.description}
                    url={cardProps.subtitle}
                  />
                );
              }

              return (
                <Card
                  key={key}
                  className={styles.nestedCard}
                  {...cardProps}
                />
              );
            })}
          </div>
        )}

        {children}
      </CardContainer>
    </Wrapper>
  );
};

export default Card;
